import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import ErrorCard from '../ErrorCard';

const Container = styled.div`
  margin: 32px 0;
`;

const Note = styled.div`
  background-color: #f8f9fa;
  border-left: 4px solid var(--zyvgo-primary, #222222);
  padding: 16px;
  margin-bottom: 24px;
  font-size: 0.9rem;
  color: #666;
  white-space: pre-line;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--zyvgo-text-primary, #222222);
`;

const SectionHint = styled.p`
  margin: 0 0 16px;
  font-size: 0.92rem;
  color: #666;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionBlock = styled.div`
  position: relative;
  cursor: pointer;
  
  input[type="radio"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
`;

const OptionLabel = styled.label`
  display: block;
  padding: 18px 24px;
  border: 2px solid ${props => props.$isSelected ? 'var(--zyvgo-primary, #222222)' : 'var(--zyvgo-border, #e1e5e9)'};
  border-radius: var(--zyvgo-radius-md, 8px);
  background-color: ${props => props.$isSelected ? 'var(--zyvgo-primary, #222222)' : '#FFFFFF'};
  color: ${props => props.$isSelected ? '#FFFFFF' : 'var(--zyvgo-text-primary, #222222)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  font-weight: ${props => props.$isSelected ? '600' : '500'};
  
  &:hover {
    border-color: var(--zyvgo-primary, #222222);
    ${props => !props.$isSelected && `
      background-color: rgba(34, 34, 34, 0.02);
    `}
  }
`;

const AliasText = styled.span`
  display: block;
  font-size: 0.85rem;
  font-weight: 400;
  margin-top: 4px;
  opacity: 0.8;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--zyvgo-primary, #222222);
  font-size: 0.9rem;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SpecialOptionTag = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(34, 34, 34, 0.08);
  color: inherit;
`;

const SpecialOptionHelp = styled.div`
  margin-top: 6px;
  font-size: 0.82rem;
  font-weight: 400;
  opacity: 0.85;
`;

const SecondarySectionTitle = styled.h4`
  margin: 22px 0 8px;
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--zyvgo-text-primary, #222222);
`;

const SecondarySectionHint = styled.p`
  margin: 0 0 14px;
  font-size: 0.86rem;
  color: #666;
`;

/**
 * MedicationDoseSelector Component
 * 
 * A two-step compound question type that produces a structured answer.
 * 
 * Step 1: Select medication (Tirzepatide, Semaglutide, etc.)
 * Step 2: Select dose (based on medication)
 * 
 * Final answer:
 * {
 *   medication: { code, label },
 *   dose: { code, label },
 *   product_context: { ...optional metadata },
 *   display_value: "{Medication} {Dose}"
 * }
 * 
 * Configuration schema (in question.validation_rules or question.answer_choices):
 * {
 *   "medications": [
 *     {
 *       "code": "tirzepatide",
 *       "display": "Tirzepatide",
 *       "aliases": ["Mounjaro", "Zepbound"],
 *       "doses": ["1.5mg", "3mg", "6mg", ...]
 *     }
 *   ],
 *   "note": "Helper text..."
 * }
 */
const MedicationDoseSelector = ({ question, value, onChange, error }) => {
    // Parse configuration from question
    // The backend serializer puts validation_rules into 'validation' field
    // Check multiple possible locations for the medications config
    const config = question.validation?.medications
        ? question.validation
        : (question.validation_rules?.medications
            ? question.validation_rules
            : (question.answer_choices?.medications ? question.answer_choices : null));

    const medications = config?.medications || [];
    const note = config?.note || question.validation?.note || question.validation_rules?.note || '';

    // Internal state for two-step selection
    const [selectedMedication, setSelectedMedication] = useState(null);
    const [selectedDose, setSelectedDose] = useState(null);
    const [directSelectedCode, setDirectSelectedCode] = useState(null);

    const isDirectOption = useCallback((med) => {
        return !!(med?.direct_select || med?.is_special_option || med?.code === 'none_of_these' || med?.code === 'other');
    }, []);

    const buildStructuredAnswer = useCallback((med, dose) => {
        if (!med) return null;

        if (isDirectOption(med)) {
            return {
                medication: {
                    code: med.code || med.display,
                    label: med.display,
                },
                dose: null,
                product_context: med.product_context || null,
                display_value: med.display,
                is_special_option: true,
            };
        }

        if (!dose) return null;

        const resolvedDose = typeof dose === 'object'
            ? dose
            : { code: String(dose), label: String(dose) };

        return {
            medication: {
                code: med.code || med.display,
                label: med.display,
                aliases: med.aliases || [],
            },
            dose: {
                code: resolvedDose.code || resolvedDose.label || String(dose),
                label: resolvedDose.label || resolvedDose.code || String(dose),
                dose_mapping_id: resolvedDose.dose_mapping_id || null,
                display_order: resolvedDose.display_order ?? null,
            },
            product_context: med.product_context || null,
            display_value: `${med.display} ${resolvedDose.label || resolvedDose.code || String(dose)}`.trim(),
        };
    }, [isDirectOption]);

    // Track if user explicitly clicked back - prevents useEffect from re-parsing
    const userClickedBackRef = React.useRef(false);
    const normalizeForMatch = useCallback((input) => {
        return String(input || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '');
    }, []);

    const standardMedications = medications.filter((med) => !isDirectOption(med));
    const specialMedications = medications.filter((med) => isDirectOption(med));

    // Parse initial value if provided (legacy string or new structured object)
    useEffect(() => {
        // Don't re-parse if user explicitly clicked back
        if (userClickedBackRef.current) {
            return;
        }

        if (!value || selectedMedication) {
            return;
        }

        if (value && typeof value === 'object') {
            const medicationCode = value?.medication?.code;
            const medicationLabel = value?.medication?.label;
            const doseCode = value?.dose?.code || value?.dose?.label;
            const med = medications.find((candidate) => {
                const candidateCode = candidate.code || candidate.display;
                return (
                    candidateCode === medicationCode ||
                    normalizeForMatch(candidate.display) === normalizeForMatch(medicationLabel)
                );
            });

            if (med) {
                if (isDirectOption(med) || value?.is_special_option) {
                    setDirectSelectedCode(med.code || med.display);
                    setSelectedMedication(null);
                    setSelectedDose(null);
                    return;
                }

                setSelectedMedication(med);
                if (doseCode) {
                    const matchedDose = (med.doses || []).find((candidateDose) => {
                        if (typeof candidateDose === 'object') {
                            return (
                                candidateDose.code === doseCode ||
                                candidateDose.label === doseCode
                            );
                        }
                        return String(candidateDose) === String(doseCode);
                    });
                    if (matchedDose) {
                        setSelectedDose(matchedDose);
                    }
                }
                return;
            }
        }

        if (value && typeof value === 'string') {
            const valueNorm = normalizeForMatch(value);
            // Try to parse existing answer
            for (const med of medications) {
                if (isDirectOption(med) && valueNorm === normalizeForMatch(med.display)) {
                    setDirectSelectedCode(med.code || med.display);
                    setSelectedMedication(null);
                    setSelectedDose(null);
                    return;
                }
                for (const dose of med.doses) {
                    const doseLabel = typeof dose === 'object' ? (dose.label || dose.code) : dose;
                    const combined = `${med.display} ${doseLabel}`;
                    if (valueNorm === normalizeForMatch(combined)) {
                        setSelectedMedication(med);
                        setSelectedDose(dose);
                        return;
                    }
                }
                // Fallback: medication-only prefill (no exact dose match)
                if (valueNorm === normalizeForMatch(med.display)) {
                    if (isDirectOption(med)) {
                        setDirectSelectedCode(med.code || med.display);
                        setSelectedMedication(null);
                        setSelectedDose(null);
                        return;
                    }
                    setSelectedMedication(med);
                    setSelectedDose(null);
                    return;
                }
            }
        }
    }, [value, medications, selectedMedication, normalizeForMatch, isDirectOption]);

    const produceCombinedAnswer = useCallback((med, dose) => {
        const answer = buildStructuredAnswer(med, dose);
        if (answer) {
            onChange(answer);
        }
    }, [buildStructuredAnswer, onChange]);

    const handleMedicationSelect = (medication) => {
        userClickedBackRef.current = false; // Reset the flag when user makes a new selection
        setDirectSelectedCode(null);
        if (isDirectOption(medication)) {
            setSelectedMedication(null);
            setSelectedDose(null);
            setDirectSelectedCode(medication.code || medication.display);
            onChange(buildStructuredAnswer(medication, null));
            return;
        }
        setSelectedMedication(medication);
        setSelectedDose(null); // Reset dose when medication changes
        // Don't call onChange yet - wait for dose selection
    };

    const handleDoseSelect = (dose) => {
        setSelectedDose(dose);
        produceCombinedAnswer(selectedMedication, dose);
    };

    const handleBack = () => {
        userClickedBackRef.current = true; // Mark that user explicitly clicked back
        setDirectSelectedCode(null);
        setSelectedMedication(null);
        setSelectedDose(null);
    };

    // Step 1: Medication selection
    if (!selectedMedication) {
        return (
            <Container>
                {note && <Note>{note}</Note>}

                <SectionTitle>Select your medication</SectionTitle>
                <SectionHint>Choose the option that best matches your most recent medication.</SectionHint>

                <OptionsContainer>
                    {standardMedications.map((med, index) => (
                        <OptionBlock key={med.code || index}>
                            <input
                                type="radio"
                                name={`medication_${question.id}`}
                                value={med.code}
                                checked={selectedMedication?.code === med.code || directSelectedCode === (med.code || med.display)}
                                onChange={() => handleMedicationSelect(med)}
                                id={`med_${question.id}_${index}`}
                            />
                            <OptionLabel
                                htmlFor={`med_${question.id}_${index}`}
                                $isSelected={selectedMedication?.code === med.code || directSelectedCode === (med.code || med.display)}
                                onClick={() => handleMedicationSelect(med)}
                            >
                                {med.display}
                                {isDirectOption(med) && (
                                    <SpecialOptionTag>Quick Option</SpecialOptionTag>
                                )}
                                {med.aliases && med.aliases.length > 0 && (
                                    <AliasText>({med.aliases.join(', ')})</AliasText>
                                )}
                                {isDirectOption(med) && med.description && (
                                    <SpecialOptionHelp>{med.description}</SpecialOptionHelp>
                                )}
                            </OptionLabel>
                        </OptionBlock>
                    ))}
                </OptionsContainer>

                {specialMedications.length > 0 && (
                    <>
                        <SecondarySectionTitle>Need another option?</SecondarySectionTitle>
                        <SecondarySectionHint>
                            Use one of these if your medication is not shown or if you are not currently taking one.
                        </SecondarySectionHint>
                        <OptionsContainer>
                            {specialMedications.map((med, index) => (
                                <OptionBlock key={med.code || `special_${index}`}>
                                    <input
                                        type="radio"
                                        name={`medication_${question.id}`}
                                        value={med.code}
                                        checked={selectedMedication?.code === med.code || directSelectedCode === (med.code || med.display)}
                                        onChange={() => handleMedicationSelect(med)}
                                        id={`med_special_${question.id}_${index}`}
                                    />
                                    <OptionLabel
                                        htmlFor={`med_special_${question.id}_${index}`}
                                        $isSelected={selectedMedication?.code === med.code || directSelectedCode === (med.code || med.display)}
                                        onClick={() => handleMedicationSelect(med)}
                                    >
                                        {med.display}
                                        <SpecialOptionTag>Quick Option</SpecialOptionTag>
                                        {med.description && (
                                            <SpecialOptionHelp>{med.description}</SpecialOptionHelp>
                                        )}
                                    </OptionLabel>
                                </OptionBlock>
                            ))}
                        </OptionsContainer>
                    </>
                )}

                {error && (
                    <ErrorCard message={error} variant="error" />
                )}
            </Container>
        );
    }

    // Step 2: Dose selection
    return (
        <Container>
            <BackButton onClick={handleBack}>
                ← Change medication
            </BackButton>

            <SectionTitle>Select your dose for {selectedMedication.display}</SectionTitle>

            <OptionsContainer>
                {selectedMedication.doses.map((dose, index) => {
                    const doseLabel = typeof dose === 'object' ? (dose.label || dose.code) : dose;
                    const isSelected = selectedDose === dose;
                    return (
                        <OptionBlock key={index}>
                            <input
                                type="radio"
                                name={`dose_${question.id}`}
                                value={doseLabel}
                                checked={isSelected}
                                onChange={() => handleDoseSelect(dose)}
                                id={`dose_${question.id}_${index}`}
                            />
                            <OptionLabel
                                htmlFor={`dose_${question.id}_${index}`}
                                $isSelected={isSelected}
                                onClick={() => handleDoseSelect(dose)}
                            >
                                {selectedMedication.display} {doseLabel}
                            </OptionLabel>
                        </OptionBlock>
                    );
                })}
            </OptionsContainer>

            {error && (
                <ErrorCard message={error} variant="error" />
            )}
        </Container>
    );
};

export default MedicationDoseSelector;
